import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/core/lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-none whitespace-nowrap text-base font-medium',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-99 transition-[color] duration-300',
    'relative overflow-hidden isolate',
    'after:absolute after:inset-0 after:-z-10',
    'after:scale-y-0',
    'after:transition-transform after:duration-700 after:ease-[cubic-bezier(.19,1,.22,1)]',
    'hover:after:scale-y-100',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-primary-0 hover:bg-primary-0 border border-primary-0 text-primary-900 after:bg-primary-900 hover:text-primary-0',
        secondary:
          'bg-secondary-100 hover:bg-primary-100 text-primary-0 after:bg-primary-0 hover:text-secondary-100',
        outline:
          'border border-input bg-primary-0 text-primary-900 after:bg-primary-900 hover:text-primary-0 hover:bg-primary-0',
        ghost:
          'shadow-none bg-primary-0 hover:bg-primary-0 text-primary-900 after:bg-primary-900 hover:text-primary-0',
        black:
          'bg-primary-900 hover:bg-primary-900 border border-primary-900 text-primary-0 after:bg-primary-0 hover:text-primary-900 hover:bg-primary-900',
        transparent:
          'bg-transparent hover:bg-transparent text-primary-900 after:bg-primary-900 hover:text-primary-0',
        transparentWhite:
          'bg-transparent hover:text-transparent border text-white border-primary-0 after:bg-white ',
      },
      size: {
        sm: 'h-9 px-3 py-2 text-sm',
        default: 'h-10 px-7 py-2 text-sm',
        lg: 'h-11 text-base px-4 py-2',
        full: 'h-13 text-base px-4 py-2 w-full',
        icon: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const fillAnimationStyles = `
@keyframes bounce-dot {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

.btn-fill::after {
  transform-origin: bottom;
}

.btn-fill:hover::after {
  transform-origin: bottom;ً
}

.btn-fill:not(:hover)::after {
  transform-origin: top;
}
`;

export function LoadingDots() {
  return (
    <span className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 rounded-full bg-current"
          style={{
            animation: `bounce-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

type CustomButtonProps = {
  loading?: boolean;
  children: ReactNode;
  asChild?: boolean;
  noAnimation?: boolean;
} & VariantProps<typeof buttonVariants> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'>;

export default function CustomButton({
  loading = false,
  children,
  className,
  variant = 'default',
  size = 'default',
  disabled,
  type = 'button',
  asChild = false,
  noAnimation = false,
  ...props
}: CustomButtonProps) {
  const Comp = asChild ? Slot : Button;

  return (
    <>
      <style>{fillAnimationStyles}</style>
      <Comp
        type={type}
        disabled={loading || disabled}
        className={cn(
          buttonVariants({ variant, size }),
          !noAnimation && 'btn-fill',
          noAnimation && 'after:hidden hover:text-inhert',
          className
        )}
        {...props}
      >
        {loading ? <LoadingDots /> : children}
      </Comp>
    </>
  );
}
